/**
 * War — server-authoritative rules engine, 2 players, one match.
 *
 * Rules implemented:
 *  - One 52-card deck split 26/26. Piles are face down: only the counts are public.
 *  - Each round the two players flip their top card one after the other (the loser of the last round flips first).
 *    The higher card (ace high) takes both cards to the bottom of their pile.
 *  - A tie is a war. Each player, in turn, either stays or RAISES by putting one extra card face down.
 *    Every player then lays 3 cards face down, plus 1 if they raised, plus 1 more if both raised (so both raising
 *    means 5 each), then flips one card. The higher flip takes the whole pot; a tie goes to war again.
 *    A raise needs at least 6 cards in your pile. A player short of cards lays fewer face-down cards but always
 *    keeps one card to flip; a player with no card left to flip loses the war.
 *  - The match ends when one player holds every card, or after `maxRounds` flips: the player with more cards wins
 *    (ties by rounds won, then by seat). The winner takes the whole prize.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface WarConfig {
  /** Flips after which the match is decided on card count. */
  maxRounds: number;
}

export type WarPhase = 'FLIP' | 'WAR' | 'MATCH_END';

/** The result of the most recent round, for the table to show. */
export interface WarRound {
  /** The cards turned up, by seat (null before that seat has flipped). */
  cards: (CardId | null)[];
  /** Seat that won, or null while a war is pending or both ran out together. */
  winner: number | null;
  /** Cards the winner collected. */
  won: number;
  /** How many wars this round went through. */
  wars: number;
  /** Face-down cards each seat laid in the last war. */
  laid: number[];
}

export interface WarState {
  config: WarConfig;
  players: string[];
  phase: WarPhase;
  /** Face-down piles, top card first. */
  piles: CardId[][];
  /** Cards on the table waiting for a winner (flipped and face-down). */
  pot: CardId[];
  /** Cards turned up this round (the current flip of each seat). */
  table: (CardId | null)[];
  /** Whose action it is (flip, or war decision). */
  turn: number;
  /** War decisions made so far this war: undefined = not yet, otherwise raised or not. */
  raised: (boolean | undefined)[];
  /** How many wars the current round has been through. */
  wars: number;
  /** Face-down cards each seat laid in the current war (public, for display). */
  laid: number[];
  last: WarRound | null;
  rounds: number;
  roundsWon: number[];
}

export type WarAction = { type: 'flip' } | { type: 'stay' } | { type: 'raise' };

export interface WarLegal {
  flip: boolean;
  stay: boolean;
  raise: boolean;
}

export interface WarView {
  gameType: 'WAR';
  config: WarConfig;
  seat: number | null;
  phase: WarPhase;
  /** Cards in each seat's pile (their order is never shown). */
  counts: number[];
  table: (CardId | null)[];
  potSize: number;
  turn: number;
  raised: (boolean | undefined)[];
  wars: number;
  laid: number[];
  last: WarRound | null;
  rounds: number;
  roundsWon: number[];
}

const DEFAULTS: WarConfig = { maxRounds: 200 };
const SEATS = 2;
const BASE_DOWN = 3;
const RAISE_MIN_PILE = 6;

export const warRank = (card: CardId): number => parseCard(card).rank;

/** Cards each seat would lay face down in the current war given the raise decisions. */
export function warFaceDown(raised: readonly (boolean | undefined)[], pile: number, seat: number): number {
  const both = raised[0] === true && raised[1] === true;
  const wanted = BASE_DOWN + (raised[seat] ? 1 : 0) + (both ? 1 : 0);
  return Math.min(wanted, Math.max(0, pile - 1));
}

function finish(state: WarState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  events.push({ type: 'matchEnd', standings: standings(state) });
}

/** After a round fully resolves: decide whether the match goes on. */
function afterRound(state: WarState, events: EngineEvent[]): void {
  state.rounds++;
  state.phase = 'FLIP';
  state.table = [null, null];
  state.raised = [undefined, undefined];
  state.laid = [0, 0];
  state.wars = 0;
  if (state.piles.some((p) => p.length === 0) || state.rounds >= state.config.maxRounds) finish(state, events);
}

function award(state: WarState, winner: number, events: EngineEvent[]): void {
  const won = state.pot.length;
  state.piles[winner]!.push(...state.pot);
  state.pot = [];
  state.roundsWon[winner]!++;
  state.last = { cards: [...state.table], winner, won, wars: state.wars, laid: [...state.laid] };
  events.push({ type: 'roundWin', seat: winner, won, wars: state.wars });
  state.turn = 1 - winner;
  afterRound(state, events);
}

/** Both players have flipped (or flipped their war card): compare. */
function compare(state: WarState, events: EngineEvent[]): void {
  const a = state.table[0]!;
  const b = state.table[1]!;
  const ra = warRank(a);
  const rb = warRank(b);
  if (ra !== rb) return award(state, ra > rb ? 0 : 1, events);
  // A tie: war, unless somebody has no card left to fight with.
  const empty = state.piles.map((p) => p.length === 0);
  if (empty[0] && empty[1]) {
    // Both ran out together: the pot is shared out card by card.
    state.pot.forEach((c, i) => state.piles[i % 2]!.push(c));
    state.pot = [];
    state.last = { cards: [...state.table], winner: null, won: 0, wars: state.wars, laid: [...state.laid] };
    afterRound(state, events);
    finish(state, events);
    return;
  }
  if (empty[0] || empty[1]) return award(state, empty[0] ? 1 : 0, events);
  state.phase = 'WAR';
  state.raised = [undefined, undefined];
  state.last = { cards: [...state.table], winner: null, won: 0, wars: state.wars, laid: [...state.laid] };
  events.push({ type: 'war', cards: [...state.table] });
}

/** Both war decisions are in: lay the face-down cards and turn up the war cards. */
function fightWar(state: WarState, events: EngineEvent[]): void {
  state.wars++;
  for (let s = 0; s < SEATS; s++) {
    const down = warFaceDown(state.raised, state.piles[s]!.length, s);
    state.pot.push(...state.piles[s]!.splice(0, down));
    state.laid[s] = down;
  }
  for (let s = 0; s < SEATS; s++) {
    const card = state.piles[s]!.shift()!;
    state.table[s] = card;
    state.pot.push(card);
  }
  events.push({ type: 'warFlip', cards: [...state.table], laid: [...state.laid] });
  state.raised = [undefined, undefined];
  compare(state, events);
}

export const warEngine: GameEngine<WarConfig, WarState, WarAction, WarView, WarLegal> = {
  gameType: 'WAR',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxRounds', c.maxRounds, 10, 2000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'War needs exactly 2 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    return {
      config,
      players,
      phase: 'FLIP',
      piles: [deck.slice(0, 26), deck.slice(26)],
      pot: [],
      table: [null, null],
      turn: rng.int(SEATS),
      raised: [undefined, undefined],
      wars: 0,
      laid: [0, 0],
      last: null,
      rounds: 0,
      roundsWon: [0, 0],
    };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'flip' || raw.type === 'stay' || raw.type === 'raise') return { type: raw.type };
    throw new EngineError('INVALID_ACTION', 'Unknown War action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (state.phase === 'FLIP') {
      assertEngine(action.type === 'flip', 'ILLEGAL_MOVE', 'Flip your card');
      const card = state.piles[seat]!.shift()!;
      state.table[seat] = card;
      state.pot.push(card);
      events.push({ type: 'flip', seat, card });
      if (state.table[1 - seat] === null) {
        state.turn = 1 - seat;
        state.last = null;
      } else compare(state, events);
      return events;
    }

    // WAR: stay or raise.
    assertEngine(action.type === 'stay' || action.type === 'raise', 'ILLEGAL_MOVE', 'Stay or raise');
    if (action.type === 'raise') assertEngine(state.piles[seat]!.length >= RAISE_MIN_PILE, 'ILLEGAL_MOVE', 'You need at least 6 cards to raise');
    state.raised[seat] = action.type === 'raise';
    events.push({ type: 'warChoice', seat, raise: action.type === 'raise' });
    if (state.raised[1 - seat] === undefined) state.turn = 1 - seat;
    else fightWar(state, events);
    return events;
  },

  getActors(state) {
    return state.phase === 'MATCH_END' ? [] : [state.turn];
  },

  getLegalActions(state, seat) {
    if (state.phase === 'MATCH_END' || seat !== state.turn) return { flip: false, stay: false, raise: false };
    if (state.phase === 'FLIP') return { flip: true, stay: false, raise: false };
    return { flip: false, stay: true, raise: state.piles[seat]!.length >= RAISE_MIN_PILE };
  },

  getTimeoutAction(state) {
    return { type: state.phase === 'WAR' ? 'stay' : 'flip' };
  },

  getView(state, seat) {
    return {
      gameType: 'WAR',
      config: state.config,
      seat,
      phase: state.phase,
      counts: state.piles.map((p) => p.length),
      table: [...state.table],
      potSize: state.pot.length,
      turn: state.turn,
      raised: [...state.raised],
      wars: state.wars,
      laid: [...state.laid],
      last: state.last ? { ...state.last, cards: [...state.last.cards] } : null,
      rounds: state.rounds,
      roundsWon: [...state.roundsWon],
    };
  },

  getSystemStep: () => null,

  runSystemStep: () => [],

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.rounds,
};

/** More cards wins; ties by rounds won, then by seat, so both places are distinct. */
function standings(state: WarState): Standing[] {
  return [0, 1]
    .sort((a, b) => state.piles[b]!.length - state.piles[a]!.length || state.roundsWon[b]! - state.roundsWon[a]! || a - b)
    .map((seat, i) => ({ seat, place: i + 1, score: state.piles[seat]!.length }));
}
